import { Button } from '@mui/material';
import { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MAIN_CONTENT_ID } from '../../constants/Accessibility';
import { focusFirstTabbableInTarget } from '../../utils/AccessibilityUtils';

type SkipToMainContentProps = {
  targetId?: string;
  triggerSelector?: string;
  onSkip?: () => void;
};

export default function SkipToMainContent({ 
  targetId = MAIN_CONTENT_ID,
  triggerSelector = '[data-test="student-toolbar"]',
  onSkip 
}: SkipToMainContentProps) {
  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const [hasShownSkip, setHasShownSkip] = useState(false);
  const location = useLocation();

  const handleSkip = () => {
    const hasFocused = focusFirstTabbableInTarget({
      targetId,
    });

    if (hasFocused) {
      setHasShownSkip(false); // Reset for next navigation session
      onSkip?.();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSkip();
    }
  };

  useEffect(() => {
    const toolbarElement = document.querySelector(triggerSelector);
    if (!toolbarElement) return;

    let tabPressed = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        tabPressed = true;
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if focus entered toolbar via Tab and skip hasn't been shown yet
      if (
        tabPressed &&
        !hasShownSkip &&
        toolbarElement.contains(target) && 
        skipButtonRef.current && 
        target !== skipButtonRef.current
      ) {
        setHasShownSkip(true);
      }
      
      tabPressed = false; // Reset after processing
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      
      // Reset when focus leaves the toolbar completely
      if (!relatedTarget || (!toolbarElement.contains(relatedTarget) && relatedTarget !== skipButtonRef.current)) {
        setHasShownSkip(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // @ts-ignore
    toolbarElement.addEventListener('focusin', handleFocusIn);
    // @ts-ignore
    toolbarElement.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // @ts-ignore
      toolbarElement.removeEventListener('focusin', handleFocusIn);
      // @ts-ignore
      toolbarElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [triggerSelector, hasShownSkip, location.pathname]);

  // Reset skip state when route changes
  useEffect(() => {
    setHasShownSkip(false);
  }, [location.pathname]);

  return (
    <Button
      ref={skipButtonRef}
      onClick={handleSkip}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      sx={{
        position: 'absolute',
        top: -1000,
        left: 10,
        zIndex: 9999,
        backgroundColor: 'primary.main',
        color: 'white',
        padding: '8px 16px',
        fontSize: '14px',
        border: '2px solid transparent',
        '&:focus': {
          top: 10,
        },
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
      data-test="skip-to-main-content"
    >
      Skip to main content
    </Button>
  );
}